import { useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'

// Guard browser and in-app navigation while edits are unsaved.
export default function useUnsavedGuard({ dirty, onSave, onDiscard }) {
  const [guardOpen, setGuardOpen] = useState(false)
  const saveAndContinueRef = useRef(false)
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname)

  useEffect(() => {
    if (blocker.state === 'blocked') setGuardOpen(true)
  }, [blocker.state])

  useEffect(() => {
    if (!dirty) return undefined
    const onBeforeUnload = e => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  function keepEditing() {
    setGuardOpen(false)
    if (blocker.state === 'blocked') blocker.reset()
  }

  function discard() {
    setGuardOpen(false)
    onDiscard()
    // Continue after the clean state renders.
    if (blocker.state === 'blocked') setTimeout(() => blocker.proceed(), 0)
  }

  async function saveAndContinue() {
    saveAndContinueRef.current = true
    const ok = await onSave()
    saveAndContinueRef.current = false
    setGuardOpen(false)
    if (ok && blocker.state === 'blocked') setTimeout(() => blocker.proceed(), 0)
    else if (blocker.state === 'blocked') blocker.reset()
  }

  return { guardOpen, keepEditing, discard, saveAndContinue, isSavingToContinue: () => saveAndContinueRef.current }
}
